
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Mutation, Action, State } from "vuex-class"

// @ts-ignore
import VuePerfectScrollbar from "vue-perfect-scrollbar"
import vSelect from "vue-select"

// @ts-ignore
import QRCode from "qrcode"

import moment from "moment"

import CaseLinks from "./CaseLinks.vue"
import CaseUsers from "@/components/CaseDetails/CaseUsers.vue"
import Invite from "@/components/CaseDetails/Users/Invite.vue"
import CaseActions from "@/components/CaseDetails/CaseActions.vue"
import CaseFields from "@/components/CaseDetails/CaseFields.vue"
import CaseMergeModal from "@/components/CaseDetails/CaseMergeModal.vue"
import Tags from "@/components/Fields/Tags.vue"
import AreaSelect from "@/components/Fields/AreaSelect.vue"
import CaseHistory from "@/components/CaseDetails/CaseHistory.vue"
import CCEventModal from "@/components/CaseDetails/CCEvent/CCEventModal.vue"
import UserImage from "@/components/UserImage.vue"
import DownloadTemplateSelect from "@/components/Fields/DownloadTemplateSelect.vue"
import SendEmailModal from "./SendEmailModal.vue"

import CaseFile from "@/types/caseFile"

import { Field, Area, Template, CaseUser, CaseField, Status, Severity, TemplateDetails, CaseDetails } from "@/types"
import { CaseEvent, CaseEvents } from "@/types/originatingEvents"
import User from "@/types/user"

import api from "@/services/api"
import SkeletonLoader from "../SkeletonLoader.vue"
import { BModal } from "bootstrap-vue"

import { IntegrationOutOption } from "@/types/integration"
import { TenantFeatures } from "@/types/tenantFeatures"

const caseStore = namespace("cases")

@Component({
	components: {
		"user-image": UserImage,
		"case-users": CaseUsers,
		"scrollbar": VuePerfectScrollbar,
		"tags": Tags,
		"v-select": vSelect,
		"invite-user": Invite,
		"case-links": CaseLinks,
		"case-actions": CaseActions,
		"area-select": AreaSelect,
		"case-fields": CaseFields,
		"loader": SkeletonLoader,
		"case-merge-modal": CaseMergeModal,
		"download-template-select": DownloadTemplateSelect,
		"case-history": CaseHistory,
		"send-email-modal": SendEmailModal
	}
})
export default class CaseSideBar extends Vue {
	$refs!: {
		confirmCloseModal: BModal
		caseMergeModal: CaseMergeModal
		caseHistory: CaseHistory
	}

	@caseStore.Getter("getStatuses") public statuses!: Status[];
	@caseStore.Getter("getSeverities") public severities!: Severity[];
	@caseStore.State("areas") public areas!: Area[];

	@Prop({ default: () => null })
	public caseEvents!: CaseEvents | null;

	@caseStore.Action public caseOpen!: (caseId: string) => void;

	@State
	public me!: User;

	@caseStore.Mutation
	public setShowCCEvent!: (ccEvent: CaseEvent) => void;

	@Getter("getCaseName") getCaseName!: string;
	@Getter("getRecordName") getRecordName!: string;
	@Getter isFeatureEnabled!: (featureKey: TenantFeatures) => boolean;

	@Prop({ default: false })
	canEditCurrentCase!: boolean;

	@Prop()
	public currentCase!: CaseDetails

	@Prop()
	public template!: TemplateDetails

	@Prop()
	public caseFiles!: CaseFile[];

	@Prop({ default: true })
	canUploadFiles!: boolean

	@Prop({ default: false })
	editAfterClosePermissions!: boolean

	@Prop({ default: false })
	canMakeClosedChanges!: boolean

	@Prop({ default: () => [] })
	caseIntegrationLinks!: IntegrationOutOption[]

  @Prop({ type: Boolean, default: false })
	canCreateComments!: boolean;

	@Prop({ default: true })
	public showAreas!: boolean

	public get anyUserRoles () {
		if (!this.currentCase || !this.currentCase.fields) return false
		return Object.keys(this.currentCase.fields)
			.some(fieldId => this.currentCase.fields[fieldId].field.dataType === "user-role")
	}

	public get qrImage () {
		let data: string | undefined

		QRCode.toDataURL(location.href, { width: 300 }, (err: Error | null, dataUrl: string | undefined) => {
			if (err) {
				console.error("Error generating QR code:", err)
				return null
			}

			data = dataUrl
		})

		return data
	}

	private get caseFieldsNotValidated (): CaseField[] {
		if (!this.currentCase || !this.currentCase.fields) {
			return []
		}

		let allFields: CaseField[] = []
		if (this.currentCase && this.currentCase.fields) {
			Object.keys(this.currentCase.fields).forEach(fieldId => {
				if (this.currentCase) {
					allFields.push(this.currentCase.fields![fieldId])
				}
			})
		}

		const outstandingFields = allFields.filter(field => (!this.validateCaseField(field)))

		return outstandingFields
	}

	/**
	 * Returns true if the field is valid.
	 */
	private validateCaseField (field: CaseField) {
		if (field.noViewAccess) { return true } 	// If the field is hidden.

		if ((this.currentCase.isCase && !field.requiredCase) || (!this.currentCase.isCase && !field.requiredRecord)) { return true }	// If field is NOT required.

		if ((field.value != null && field.value !== undefined && field.value !== false && field.value !== "") || (Array.isArray(field.value) && field.value.length === 0)) {
			return true 	// If field is visible, required and not null, empty or false.
		}

		return false
	}

	private get caseActionsValidated (): boolean {
		if (!this.currentCase || !this.currentCase.actions) {
			return true
		}

		const isCase: boolean = this.currentCase.isCase
		return !this.currentCase.actions.some(action => ((action.requiredRecord && isCase === false) ||
															(action.requiredCase && isCase)) &&
															action.completedBy === null)
	}

	private get integrationOptionLinks (): Set<string> {
		return new Set<string>(this.caseIntegrationLinks
			? this.caseIntegrationLinks.map(link => link.integrationOut)
			: [])
	}

	private onCaseChanged () {
		if (this.currentCase.id) {
			api.saveCase(this.currentCase)
		}
	}

	public openEvent (event: CaseEvent) {
		this.setShowCCEvent(event)
	}

	public updateLocationsList () {
		if (this.currentCase.id) {
			api.saveCase(this.currentCase)
		}
	}

	public makeCase () {
		api.recordToCase(this.currentCase.id!)
	}

	public showAreaDetails (area: Area): void {

	}

	public get closeCaseButtonToolTipTitle (): string {
		if (this.caseFieldsNotValidated && this.caseFieldsNotValidated.length > 0 && this.caseActionsValidated === false) {
			return "Still requires " + this.caseFieldsNotValidated.map(f => f.field.title).join(", ") + " and actions to be completed."
		} else if (this.caseFieldsNotValidated && this.caseFieldsNotValidated.length > 0) {
			return "Still requires " + this.caseFieldsNotValidated.map(f => f.field.title).join(", ") + " to be completed."
		} else if (this.caseActionsValidated === false) {
			return "All field actions required"
		}

		return ""
	}

	public get closeConfirmTitle () {
		return this.currentCase.isCase
			? "Close " + this.getCaseName
			: "Close " + this.getRecordName
	}

	public get caseCreatedLocalTime (): string {
		return this.currentCase.created ? moment(this.currentCase.created).format("l LTS") : ""
	}
	public get caseClosedLocalTime (): string {
		return this.currentCase.closed ? moment(this.currentCase.closed).format("l LTS") : ""
	}

	private confirmCloseCase (id: string): void {
		this.$refs.confirmCloseModal.show()
	}

	public makeClosedChanges () {
		this.$emit("makeClosedChanges")
	}

	private showMarkSensitiveConfirmation () {
		if (this.currentCase.id) {
			this.markSensitiveModalShown = true
		}
	}

	private markSensitiveModalShown = false

	private async markSensitive () {
		if (this.currentCase.id) {
			try {
				await api.markAsSensitive(this.currentCase.id)
				await this.caseOpen(this.currentCase.caseNumber!)
			} catch (err) {
				console.log(err)
			}
		}
		this.markSensitiveModalShown = false
	}

	private async GenerateQRCode () {
		this.$bvModal.show("QR-Modal")
	}

	private mergeRecords () {
		this.$refs.caseMergeModal.open()
	}

	private viewHistory (): void {
		this.$refs.caseHistory.open()
	}

	public showCadence (): boolean {
		return (!!this.template && !!this.template.cadenceDetails &&
			this.template.cadenceDetails.cadencePeriod !== null &&
			this.template.cadenceDetails.cadencePeriod !== undefined &&
			this.currentCase.isClosed === false)
	}

	public showDisableCadence (): boolean {
		const disablePermissions = this.template!.cadenceDetails!.disablePermissions ? this.template!.cadenceDetails!.disablePermissions : []
		return this.showCadence() && (!disablePermissions || disablePermissions.length === 0 ||
			disablePermissions.some((t: any) => this.me.teams.find((team: any) => team.id === t.id)))
	}

	public doCaseClose (closeCaseId: string) {
		if (this.currentCase.id === closeCaseId) {
			this.$emit("close", this.currentCase)
		}
	}

	public async hideFromInsights () {
		if (!this.currentCase.id) {
			return
		}

		this.$set(this.currentCase, "hiddenFromInsights", true)
		await api.saveCase(this.currentCase)
		this.hideFromInsightsModalShown = false
	}

	public async unhideFromInsights () {
		if (!this.currentCase.id) {
			return
		}
		this.$set(this.currentCase, "hiddenFromInsights", false)
		await api.saveCase(this.currentCase)
	}

	/** Shows the "hide from insights" confirm modal. */
	private hideFromInsightsModalShown = false
	private showHideFromInsightsConfirmation () {
		this.hideFromInsightsModalShown = true
	}

	/** Shows the delete confirm modal. */
	private deleteCaseModalShown = false
	private showDeleteCaseModal () {
		this.deleteCaseModalShown = true
	}

	/** Delete confirmation text. The user is require to type the name of the case in order to delete. */
	private deleteConfirmText = ""

	/** Deletes the current case. */
	public async deleteCase () {
		if (this.currentCase.id &&
			((this.currentCase.title && this.deleteConfirmText === this.currentCase.title) ||
			(!this.currentCase.title && this.deleteConfirmText === "delete"))
		) {
			await api.deleteCase(this.currentCase.id!)
			window.location.reload()
		}
	}

	public pushTo (outputOption: IntegrationOutOption) {
		this.$emit("integrationPush", outputOption)
	}

	private caseOrRecordText () : string {
		return this.currentCase.isCase ? "Case" : "Record"
	}

	private printPage () : void {
		window.print()
	}

	private downloadModalShown = false;
	private get isPdfTemplateEnabled (): boolean {
		return this.isFeatureEnabled(TenantFeatures.PdfTemplates)
	}

	private showDownloadModal () : void {
		this.downloadModalShown = true
	}

	/** Send case as email. */
	private isSendEmailModalVisible: boolean = false
	private get isSendEmailEnabled (): boolean {
		return this.isFeatureEnabled(TenantFeatures.SendEmail)
	}

	private showSendEmailModal () {
		this.isSendEmailModalVisible = true
	}
}

